import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { getSignLibrary } from '../services/api';
import { TrafficSpecialistChat } from './TrafficSpecialistChat';
import { QuotaWarning } from './QuotaWarning';
import { ExportPlanPanel } from './ExportPlanPanel';
import type { ArrowDescriptor } from '../types/arrows';
import type { DelineatorLine, DelineatorDeviceType } from '../types/delineators';
import type { ToolMode } from './BottomRightTools';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrafficIcon from '@mui/icons-material/Traffic';
import { useAuth } from '../hooks/useAuth';
import { getUserCustomSigns, type CustomCompositeSign } from '../services/customSigns';
import { getUserFavorites, toggleSignFavorite as apiToggleSignFavorite, toggleCustomSignFavorite as apiToggleCustomSignFavorite } from '../services/favorites';
import { PropertiesPanel } from './PropertiesPanel';
import type { SelectedElement } from '../types/selection';
import TuneIcon from '@mui/icons-material/Tune';

type SignItem = { code: string; svg: string; description?: string; category?: string; subcategory?: string };

type ActivePanel = 'library' | 'chat' | 'quota' | 'export' | 'properties' | null;

type DraggableSignLibraryProps = {
  activePanel: ActivePanel;
  onOpenLibrary: () => void;
  onOpenTrafficSpecialist: () => void;
  onOpenProperties: () => void;
  onOpenQuota: () => void;
  onClosePanel: () => void;
  onOpenExport: () => void;
  signPlacement?: any;
  workzones?: any[];
  arrows?: ArrowDescriptor[];
  onArrowDistanceChange?: (id: string | 'all', distance: number) => void;
  onArrowLabelVisibilityChange?: (id: string, show: boolean) => void;
  onArrowDelete?: (id: string) => void;
  // Delineator props
  currentTool?: ToolMode;
  onDelineatorToolActivate?: (deviceType: DelineatorDeviceType) => void;
  onDelineatorToolDeactivate?: () => void;
  delineatorLines?: DelineatorLine[];
  onDelineatorDelete?: (id: string) => void;
  onDelineatorUpdate?: (id: string, updates: Partial<DelineatorLine>) => void;
  // Properties panel props
  selectedElement?: SelectedElement;
  onSignRotate?: (id: string, rotation: number) => void;
  onSignResize?: (id: string, scale: number) => void;
  onSignMove?: (id: string, position: { lat: number; lng: number }) => void;
  onSignCalloutMove?: (id: string, offset: { x: number; y: number }) => void;
  onSignDelete?: (id: string) => void;
  onWorkzoneDelete?: (id: string) => void;
  // Road overlay props
  onRoadOverlaySettingsChange?: (settings: any) => void;
};

const CATEGORY_ORDER = [
  'Multi-Message Board Signs',
  '🛑 Regulatory & Control',
  '🚧 Work Zone & Temporary',
  '⚠️ Warning & Advisory',
  '➡️ Directional & Guide',
];

const quotaDetails = {
  current_usage: 76,
  limit: 100,
  plan: 'Pro',
  requestedFormat: undefined,
  allowedFormats: undefined,
};

export const DraggableSignLibrary: React.FC<DraggableSignLibraryProps> = ({
  activePanel,
  onOpenLibrary,
  onOpenTrafficSpecialist,
  onOpenProperties,
  onOpenQuota,
  onClosePanel,
  onOpenExport,
  signPlacement,
  workzones = [],
  arrows = [],
  onArrowDistanceChange,
  onArrowLabelVisibilityChange,
  onArrowDelete,
  currentTool,
  onDelineatorToolActivate,
  onDelineatorToolDeactivate,
  delineatorLines = [],
  onDelineatorDelete,
  onDelineatorUpdate,
  selectedElement,
  onSignRotate,
  onSignResize,
  onSignMove,
  onSignCalloutMove,
  onSignDelete,
  onWorkzoneDelete,
  onRoadOverlaySettingsChange,
}) => {
  const { user, getToken } = useAuth();
  
  const svgToDataUrl = (svg: string) => {
    try {
      const base64 = window.btoa(unescape(encodeURIComponent(svg)));
      return `data:image/svg+xml;base64,${base64}`;
    } catch (e) {
      return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    }
  };

  const [signLibrary, setSignLibrary] = useState<Record<string, any> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [favoriteCodes, setFavoriteCodes] = useState<string[]>([]);  // Regular sign codes
  const [favoriteCustomSignIds, setFavoriteCustomSignIds] = useState<string[]>([]);  // Custom sign IDs
  
  // New state variables for filtering
  const [filterMode, setFilterMode] = useState<0 | 1>(0); // 0 = subcategory (DEFAULT), 1 = all+favorites
  const [customSigns, setCustomSigns] = useState<CustomCompositeSign[]>([]);
  const [loadingCustomSigns, setLoadingCustomSigns] = useState(false);
  
  // Track active delineator device type
  const [activeDelineatorDevice, setActiveDelineatorDevice] = useState<DelineatorDeviceType | null>(null);

  useEffect(() => {
    const fetchLibrary = async () => {
      setIsLoading(true);
      try {
        const library = await getSignLibrary();
        setSignLibrary(library);
      } catch (error) {
        console.error('Failed to fetch sign library:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLibrary();
  }, []);

  // Load user's custom composite signs  
  useEffect(() => {
    const loadCustomSigns = async () => {
      if (!user || activePanel !== 'library') return;
      
      try {
        setLoadingCustomSigns(true);
        const token = await getToken();
        if (token) {
          const signs = await getUserCustomSigns(token);
          setCustomSigns(signs);
        }
      } catch (error) {
        console.error('Error loading custom signs:', error);
      } finally {
        setLoadingCustomSigns(false);
      }
    };

    loadCustomSigns();
  }, [user, activePanel, getToken]);

  // Reload custom signs every time the library panel opens (ensures fresh data after saves)
  useEffect(() => {
    if (activePanel !== 'library' || !user) {
      return; // Early return if conditions not met
    }

    const reloadInterval = setInterval(async () => {
      try {
        const token = await getToken();
        if (!token) {
          console.warn('No auth token available for reload');
          return;
        }
        
        const signs = await getUserCustomSigns(token);
        setCustomSigns(signs);
      } catch (error) {
        console.error('Error reloading custom signs:', error);
      }
    }, 5000); // Reload every 5 seconds while panel is open

    // Cleanup function to clear interval when panel closes or user logs out
    return () => {
      clearInterval(reloadInterval);
    };
  }, [activePanel, user, getToken]);

  // Load user's favorites from database
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user) return;
      
      try {
        const token = await getToken();
        if (token) {
          const favorites = await getUserFavorites(token);
          setFavoriteCodes(favorites.sign_codes || []);
          setFavoriteCustomSignIds(favorites.custom_sign_ids || []);
        }
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    };

    loadFavorites();
  }, [user, getToken]);

  const signsArray = useMemo(() => {
    if (!signLibrary) return [];
    const signs = Object.entries(signLibrary).map(([code, info]) => ({
      code,
      svg: (info?.svg as string) || '',
      description: (info?.description as string) || '',
      category: (info?.category as string) || 'General',
      subcategory: (info?.subcategory as string) || 'Other',
      subcategoryDescription: (info?.subcategory_description as string) || 'Other',
      states: (info?.states as string[]) || [],
      stateNames: (info?.state_names as string[]) || [],
    }));
    
    return signs;
  }, [signLibrary]);

  // No default favorites - users must explicitly add their own

  const filteredSigns = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    let filtered = signsArray;
    
    // Apply search filter
    if (normalizedQuery) {
      filtered = filtered.filter(sign => {
        const description = sign.description?.toLowerCase() || '';
        return (
          sign.code.toLowerCase().includes(normalizedQuery) ||
          description.includes(normalizedQuery) ||
          sign.category?.toLowerCase().includes(normalizedQuery) ||
          sign.subcategory?.toLowerCase().includes(normalizedQuery)
        );
      });
    }
    
    return filtered;
  }, [signsArray, query]);

  const favoriteSigns = filteredSigns.filter(sign => favoriteCodes.includes(sign.code));
  const regularSigns = filteredSigns.filter(sign => !favoriteCodes.includes(sign.code));
  
  // Filter custom signs by search query
  const filteredCustomSigns = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return customSigns;
    
    return customSigns.filter(sign => 
      sign.name.toLowerCase().includes(normalizedQuery)
    );
  }, [customSigns, query]);
  
  const favoriteCustomSigns = filteredCustomSigns.filter(sign => favoriteCustomSignIds.includes(sign.id || ''));
  
  // CATEGORY_ORDER is defined at the top of the file (line 58) with emojis

  // Group signs by subcategory
  const signsBySubcategory = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    filteredSigns.forEach(sign => {
      const subcat = sign.subcategory || 'Other';
      if (!grouped[subcat]) {
        grouped[subcat] = [];
      }
      grouped[subcat].push(sign);
    });
    
    // Sort each group by sign code
    Object.keys(grouped).forEach(subcat => {
      grouped[subcat].sort((a, b) => a.code.localeCompare(b.code));
    });
    
    return grouped;
  }, [filteredSigns]);

  const toggleFavorite = async (code: string) => {
    // Optimistically update UI
    setFavoriteCodes(prev =>
      prev.includes(code) ? prev.filter(item => item !== code) : [...prev, code]
    );

    // Persist to database
    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        // Revert optimistic update if no token
        setFavoriteCodes(prev =>
          prev.includes(code) ? prev.filter(item => item !== code) : [...prev, code]
        );
        return;
      }
      
      const updatedFavorites = await apiToggleSignFavorite(token, code);
      // Update state with server response to ensure sync
      setFavoriteCodes(updatedFavorites.sign_codes || []);
      setFavoriteCustomSignIds(updatedFavorites.custom_sign_ids || []);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert on error
      setFavoriteCodes(prev =>
        prev.includes(code) ? prev.filter(item => item !== code) : [...prev, code]
      );
    }
  };

  const toggleCustomSignFavorite = async (customSignId: string) => {
    // Optimistically update UI
    setFavoriteCustomSignIds(prev =>
      prev.includes(customSignId) ? prev.filter(item => item !== customSignId) : [...prev, customSignId]
    );

    // Persist to database
    try {
      const token = await getToken();
      if (!token) {
        console.error('No auth token available');
        // Revert optimistic update if no token
        setFavoriteCustomSignIds(prev =>
          prev.includes(customSignId) ? prev.filter(item => item !== customSignId) : [...prev, customSignId]
        );
        return;
      }
      
      const updatedFavorites = await apiToggleCustomSignFavorite(token, customSignId);
      // Update state with server response to ensure sync
      setFavoriteCodes(updatedFavorites.sign_codes || []);
      setFavoriteCustomSignIds(updatedFavorites.custom_sign_ids || []);
    } catch (error) {
      console.error('Error toggling custom sign favorite:', error);
      // Revert on error
      setFavoriteCustomSignIds(prev =>
        prev.includes(customSignId) ? prev.filter(item => item !== customSignId) : [...prev, customSignId]
      );
    }
  };


  const renderSignCard = (sign: any) => (
    <Tooltip key={sign.code} title={sign.description || sign.code} placement="right" arrow>
      <Box
        draggable
        onDragStart={event => {
          event.dataTransfer.setData(
            'application/json',
            JSON.stringify({ type: 'sign', code: sign.code, svg: sign.svg })
          );
          event.dataTransfer.setData('signCode', sign.code);
        }}
        onContextMenu={event => {
          event.preventDefault();
          toggleFavorite(sign.code);
        }}
        sx={{
          position: 'relative',
          cursor: 'grab',
          width: 80,
          height: 35,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
          '&:hover': { transform: 'scale(1.05)' },
          '&:active': { cursor: 'grabbing', transform: 'scale(0.98)' },
        }}
      >
        <Box
          component="img"
          src={svgToDataUrl(sign.svg)}
          alt={sign.code}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
    </Tooltip>
  );

  // Render custom composite sign as draggable card
  const renderCompositeSignCard = (composite: CustomCompositeSign) => {
    // Generate actual composite SVG preview
    const containerWidth = composite.canvasWidth || 1200;
    const containerHeight = composite.canvasHeight || 900;
    
    // Create a container SVG element in DOM for proper rendering
    const containerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    containerSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    containerSvg.setAttribute('width', String(containerWidth));
    containerSvg.setAttribute('height', String(containerHeight));
    containerSvg.setAttribute('viewBox', `0 0 ${containerWidth} ${containerHeight}`);
    
    // Add background rect
    const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bgRect.setAttribute('x', '0');
    bgRect.setAttribute('y', '0');
    bgRect.setAttribute('width', String(containerWidth));
    bgRect.setAttribute('height', String(containerHeight));
    bgRect.setAttribute('fill', '#f5f5f5');
    bgRect.setAttribute('stroke', '#DC2626');
    bgRect.setAttribute('stroke-width', '4');
    containerSvg.appendChild(bgRect);
    
    // Add each component sign
    composite.components.forEach((component, index) => {
      const signData = signLibrary?.[component.signCode.toLowerCase()];
      if (!signData?.svg) return;

      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(signData.svg, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');

      if (!svgElement) return;

      const uniquePrefix = `${composite.id || 'custom'}-${component.id || component.signCode}-${index}`;
      applyUniqueIds(svgElement, uniquePrefix);

      const viewBox = svgElement.getAttribute('viewBox') || '0 0 40 40';
      const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(' ').map(Number);
      
      // Calculate scale to fit component size
      const scaleX = component.width / vbWidth;
      const scaleY = component.height / vbHeight;
      
      // Build transform with translation and scaling
      const transform = `translate(${component.x}, ${component.y}) scale(${scaleX}, ${scaleY}) translate(${-vbX}, ${-vbY})`;
      
      // Create a group with transform
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('transform', transform);
      
      // Separate style/defs from graphical elements
      const children = Array.from(svgElement.children);
      for (const child of children) {
        const tagName = child.tagName.toLowerCase();
        
        // Style and defs go to the root SVG, not in the transformed group
        if (tagName === 'style' || tagName === 'defs') {
          const clonedChild = child.cloneNode(true) as Element;
          containerSvg.appendChild(clonedChild);
        } else {
          // Graphical elements go in the transformed group
          // Clone AFTER we've already applied unique IDs to the source svgElement
          const clonedChild = child.cloneNode(true) as Element;
          g.appendChild(clonedChild);
        }
      }
      
      // Only append the group if it has children
      if (g.children.length > 0) {
        containerSvg.appendChild(g);
      }
    });
    
    // Serialize the DOM element to string
    const serializer = new XMLSerializer();
    const compositeSvg = serializer.serializeToString(containerSvg);

    const isFavorite = favoriteCustomSignIds.includes(composite.id || '');

    return (
      <Tooltip key={composite.id} title={`Custom: ${composite.name} (Right-click to ${isFavorite ? 'remove from' : 'add to'} favorites)`} placement="right" arrow>
        <Box
          draggable
          onDragStart={event => {
            event.dataTransfer.setData(
              'application/json',
              JSON.stringify({ 
                type: 'composite', 
                composite: composite,
                code: 'COMPOSITE',
                svg: compositeSvg 
              })
            );
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (composite.id) {
              toggleCustomSignFavorite(composite.id);
            }
          }}
          sx={{
            position: 'relative',
            cursor: 'grab',
            width: 80,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fff',
            border: '1px solid #ddd',
            transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
            '&:hover': { transform: 'scale(1.05)' },
            '&:active': { cursor: 'grabbing', transform: 'scale(0.98)' },
            overflow: 'hidden',
          }}
        >
          {/* Favorite indicator */}
          {isFavorite && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '50%',
                width: 16,
                height: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <StarIcon sx={{ fontSize: 12, color: '#FFD700' }} />
            </Box>
          )}
          <Box
            dangerouslySetInnerHTML={{ __html: compositeSvg }}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& > svg': {
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }
            }}
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 100,
        left: 20,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: 56,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
          borderRadius: '16px',
          border: '1px solid rgba(192, 202, 202, 0.7)',
          boxShadow: '0px 16px 36px rgba(15, 23, 42, 0.16)',
          backgroundColor: '#FFFFFF',
          flexShrink: 0,
        }}
      >
        <Tooltip title="Sign Library" placement="right">
          <IconButton
            onClick={onOpenLibrary}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: activePanel === 'library' ? '#A01E1D' : '#FFFFFF',
              color: activePanel === 'library' ? '#FFFFFF' : '#111827',
              border:
                activePanel === 'library'
                  ? '2px solid #A01E1D'
                  : '1px solid rgba(208, 213, 221, 0.6)',
              p: 0,
              flexShrink: 0,
              '&:hover': {
                bgcolor: activePanel === 'library' ? '#8B1A1A' : 'rgba(17, 24, 39, 0.05)'
              }
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 22 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ pointerEvents: 'none', display: 'block' }}
            >
              <g clipPath="url(#clip0_rail_sign)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.893 1.875C9.76987 1.87523 9.65022 1.91585 9.5524 1.99064C9.45458 2.06542 9.38401 2.17024 9.3515 2.289L7.9205 7.5H14.0795L12.6485 2.289C12.616 2.17024 12.5454 2.06542 12.4476 1.99064C12.3498 1.91585 12.2301 1.87523 12.107 1.875H9.893ZM4.73 19.125L6.068 14.25H15.932L17.27 19.125H4.73ZM2.7845 19.125L7.5425 1.7925C7.68382 1.27749 7.99037 0.823135 8.41503 0.499288C8.83968 0.175442 9.35895 2.47477e-05 9.893 0L12.107 0C13.2035 0 14.1665 0.735 14.4575 1.7925L19.2155 19.125H20.5625C20.8111 19.125 21.0496 19.2238 21.2254 19.3996C21.4012 19.5754 21.5 19.8139 21.5 20.0625C21.5 20.3111 21.4012 20.5496 21.2254 20.7254C21.0496 20.9012 20.8111 21 20.5625 21H1.4375C1.18886 21 0.950403 20.9012 0.774587 20.7254C0.598772 20.5496 0.5 20.3111 0.5 20.0625C0.5 19.8139 0.598772 19.5754 0.774587 19.3996C0.950403 19.2238 1.18886 19.125 1.4375 19.125H2.7845Z"
                  fill={activePanel === 'library' ? 'white' : '#111827'}
                />
              </g>
              <defs>
                <clipPath id="clip0_rail_sign">
                  <rect width="21" height="21" fill="white" transform="translate(0.5)" />
                </clipPath>
              </defs>
            </svg>
          </IconButton>
        </Tooltip>

        <Tooltip title="Traffic Specialist" placement="right">
          <IconButton
            onClick={onOpenTrafficSpecialist}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: activePanel === 'chat' ? '#FECACA' : '#FFFFFF',
              border:
                activePanel === 'chat'
                  ? '2px solid #DC2626'
                  : '1px solid rgba(208, 213, 221, 0.6)',
              p: 0,
              '&:hover': {
                bgcolor: activePanel === 'chat' ? '#F48B8B' : 'rgba(17, 24, 39, 0.05)'
              }
            }}
          >
            <Avatar src="/images/mark-avatar.jpg" alt="Mark" sx={{ width: 22, height: 22 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Properties" placement="right">
          <IconButton
            onClick={onOpenProperties}
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: activePanel === 'properties' ? '#DBEAFE' : '#FFFFFF',
              color: activePanel === 'properties' ? '#1D4ED8' : '#111827',
              border:
                activePanel === 'properties'
                  ? '2px solid #3B82F6'
                  : '1px solid rgba(208, 213, 221, 0.6)',
              p: 0,
              flexShrink: 0,
              '&:hover': {
                bgcolor: activePanel === 'properties' ? '#BFDBFE' : 'rgba(17, 24, 39, 0.05)'
              },
            }}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Paper>

      {activePanel && (
        <Paper
          elevation={6}
          sx={{
            width:
              activePanel === 'chat'
                ? 'min(400px, calc(100vw - 48px))'
                : 'min(320px, calc(100vw - 48px))',
            maxHeight: 'calc(100vh - 132px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '16px',
            boxShadow: '0px 16px 36px rgba(15, 23, 42, 0.16)',
            backgroundColor: '#FFFFFF',
          }}
        >
          {activePanel === 'library' && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  px: 3,
                  pt: 3,
                  pb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
                    Sign Library
                  </Typography>
                  <Tooltip 
                    title={filterMode === 0 ? 'Group by Subcategory' : 'Show All + Favorites'}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Cycle through modes: 0 -> 1 -> 0
                        setFilterMode(prev => (prev === 0 ? 1 : 0) as 0 | 1);
                      }}
                      sx={{
                        border: '1px solid rgba(209, 213, 219, 0.9)',
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        bgcolor: filterMode === 0 ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                        '&:hover': {
                          bgcolor: filterMode === 0 ? 'rgba(33, 150, 243, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                    >
                      {filterMode === 0 && <CategoryIcon sx={{ color: '#1976d2' }} />}
                      {filterMode === 1 && <ViewModuleIcon sx={{ color: '#111827' }} />}
                    </IconButton>
                  </Tooltip>
                </Box>

                <TextField
                  placeholder="Search signs"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#6B7280' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: 10,
                      bgcolor: '#F9FAFB',
                      '& input': {
                        fontWeight: 200,
                      },
                      '& fieldset': {
                        border: '1px solid rgba(209, 213, 219, 0.7)',
                      },
                    },
                  }}
                />
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: 2.5,
                  py: 2.5,
                  minHeight: 0,
                  '&::-webkit-scrollbar': {
                    width: 8,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(17, 24, 39, 0.2)',
                    borderRadius: 8,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.35)',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {/* MODE 0: Group by Subcategory */}
                {filterMode === 0 && (
                  <Box>
                    {/* Custom Signs Section (MODE 0) - FIRST */}
                    {user && filteredCustomSigns.length > 0 && (
                      <Accordion
                        defaultExpanded={false}
                        disableGutters
                        elevation={0}
                        sx={{
                          '&:before': { display: 'none' },
                          mb: 0.5,
                          border: '1px solid rgba(220, 38, 38, 0.3)',
                          borderRadius: '8px !important',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            minHeight: 48,
                            px: 2,
                            bgcolor: 'rgba(220, 38, 38, 0.05)',
                            '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.1)' },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#DC2626' }}>
                              My Signs
                            </Typography>
                            <Chip 
                              label={filteredCustomSigns.length} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                bgcolor: '#DC2626',
                                color: 'white'
                              }} 
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                          <Grid container spacing={0.5}>
                            {filteredCustomSigns.map((composite: any) => (
                              <Grid item xs={4} key={composite.id}>
                                {renderCompositeSignCard(composite)}
                              </Grid>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Delineators Section */}
                    <Accordion
                      defaultExpanded={false}
                      disableGutters
                      elevation={0}
                      sx={{
                        '&:before': { display: 'none' },
                        mb: 0.5,
                        border: currentTool === 'delineator' 
                          ? '2px solid #F97316' 
                          : '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '8px !important',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 48,
                          px: 2,
                          bgcolor: currentTool === 'delineator' 
                            ? 'rgba(249, 115, 22, 0.15)' 
                            : 'rgba(249, 115, 22, 0.05)',
                          '&:hover': { bgcolor: 'rgba(249, 115, 22, 0.1)' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <TrafficIcon sx={{ fontSize: 18, color: '#F97316' }} />
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#EA580C' }}>
                            Delineators
                          </Typography>
                          {currentTool === 'delineator' && (
                            <Chip 
                              label="Active" 
                              size="small" 
                              sx={{ 
                                height: 18, 
                                fontSize: '0.65rem',
                                bgcolor: '#F97316',
                                color: 'white',
                                ml: 'auto'
                              }} 
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1.5 }}>
                          Click a tool below, then click on the map to draw a line. Double-click to finish.
                        </Typography>
                        <Grid container spacing={1}>
                          {/* Cone Line Tool */}
                          <Grid item xs={6}>
                            <Box
                              onClick={() => {
                                if (currentTool === 'delineator' && activeDelineatorDevice === 'cone') {
                                  setActiveDelineatorDevice(null);
                                  onDelineatorToolDeactivate?.();
                                } else {
                                  setActiveDelineatorDevice('cone');
                                  onDelineatorToolActivate?.('cone');
                                }
                              }}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: currentTool === 'delineator' && activeDelineatorDevice === 'cone'
                                  ? '2px solid #F97316' 
                                  : '1px solid rgba(229, 231, 235, 0.8)',
                                bgcolor: currentTool === 'delineator' && activeDelineatorDevice === 'cone'
                                  ? 'rgba(249, 115, 22, 0.1)' 
                                  : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'rgba(249, 115, 22, 0.08)',
                                  borderColor: '#F97316',
                                },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {/* Cone SVG Icon */}
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L6 20H18L12 2Z" fill="#F97316" stroke="#EA580C" strokeWidth="1.5"/>
                                <rect x="4" y="20" width="16" height="2" rx="0.5" fill="#EA580C"/>
                                <path d="M8 12H16" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M7 16H17" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                              <Typography variant="caption" fontWeight={500} sx={{ color: '#374151' }}>
                                Cone Line
                              </Typography>
                            </Box>
                          </Grid>
                          {/* Yellow Dot Tool */}
                          <Grid item xs={6}>
                            <Box
                              onClick={() => {
                                if (currentTool === 'delineator' && activeDelineatorDevice === 'yellow_dot') {
                                  setActiveDelineatorDevice(null);
                                  onDelineatorToolDeactivate?.();
                                } else {
                                  setActiveDelineatorDevice('yellow_dot');
                                  onDelineatorToolActivate?.('yellow_dot');
                                }
                              }}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: currentTool === 'delineator' && activeDelineatorDevice === 'yellow_dot'
                                  ? '2px solid #FF4500' 
                                  : '1px solid rgba(229, 231, 235, 0.8)',
                                bgcolor: currentTool === 'delineator' && activeDelineatorDevice === 'yellow_dot'
                                  ? 'rgba(255, 69, 0, 0.1)' 
                                  : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 69, 0, 0.08)',
                                  borderColor: '#FF4500',
                                },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              {/* Traffic Yellow Dot SVG Icon - matches actual sign */}
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" fill="#E6A224"/>
                                <circle cx="12" cy="12" r="6.5" fill="#FF4500"/>
                              </svg>
                              <Typography variant="caption" fontWeight={500} sx={{ color: '#374151' }}>
                                Traffic Dot
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        
                        {/* Show existing delineator lines */}
                        {delineatorLines.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} sx={{ color: '#374151', display: 'block', mb: 1 }}>
                              Placed Lines ({delineatorLines.length})
                            </Typography>
                            {delineatorLines.map((line, index) => (
                              <Box
                                key={line.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  mb: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(249, 115, 22, 0.05)',
                                  border: '1px solid rgba(249, 115, 22, 0.2)',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TrafficIcon sx={{ fontSize: 14, color: '#F97316' }} />
                                  <Typography variant="caption" sx={{ color: '#374151' }}>
                                    {line.deviceType.charAt(0).toUpperCase() + line.deviceType.slice(1)} Line {index + 1}
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => onDelineatorDelete?.(line.id)}
                                  sx={{ 
                                    width: 24, 
                                    height: 24,
                                    '&:hover': { color: '#DC2626' }
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {/* Regular Sign Categories */}
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : (
                      Object.entries(signsBySubcategory)
                        .sort(([catA], [catB]) => {
                          const indexA = CATEGORY_ORDER.indexOf(catA);
                          const indexB = CATEGORY_ORDER.indexOf(catB);
                          // If both are in the order array, sort by their index
                          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                          // If only one is in the array, prioritize it
                          if (indexA !== -1) return -1;
                          if (indexB !== -1) return 1;
                          // Otherwise, sort alphabetically
                          return catA.localeCompare(catB);
                        })
                        .map(([subcategory, signs]) => (
                          <Accordion
                            key={subcategory}
                            defaultExpanded={false}
                            disableGutters
                            elevation={0}
                            sx={{
                              '&:before': { display: 'none' },
                              mb: 0.5,
                              border: '1px solid rgba(229, 231, 235, 0.8)',
                              borderRadius: '8px !important',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                minHeight: 48,
                                px: 2,
                                '&:hover': { bgcolor: '#f8f8f8' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {signs[0]?.subcategoryDescription || subcategory}
                                </Typography>
                                <Chip label={signs.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                              <Grid container spacing={0.5}>
                                {signs.map((sign: any) => (
                                  <Grid item xs={4} key={sign.code}>
                                    {renderSignCard(sign)}
                                  </Grid>
                                ))}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>
                        ))
                    )}
                  </Box>
                )}

                {/* MODE 1: Show All + Favorites */}
                {filterMode === 1 && (
                  <>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#323232',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          mb: 0.75,
                        }}
                      >
                        Favorites
                      </Typography>
                      <Grid container spacing={0.5}>
                        {/* Show favorite signs */}
                        {favoriteSigns.length > 0 && favoriteSigns.map(sign => (
                          <Grid item xs={4} key={sign.code}>
                            {renderSignCard(sign)}
                          </Grid>
                        ))}
                        
                        {/* Show favorite custom composite signs */}
                        {user && favoriteCustomSigns.length > 0 && favoriteCustomSigns.map(composite => (
                          <Grid item xs={4} key={composite.id}>
                            {renderCompositeSignCard(composite)}
                          </Grid>
                        ))}
                        
                        {/* Show message if no favorites */}
                        {favoriteSigns.length === 0 && favoriteCustomSigns.length === 0 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Right-click any sign (regular or custom) to add it to favorites.
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#323232',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          mb: 1,
                        }}
                      >
                        All Signs
                      </Typography>
                      {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : (
                        <Grid container spacing={0.5}>
                          {regularSigns.map(sign => (
                            <Grid item xs={4} key={sign.code}>
                              {renderSignCard(sign)}
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Box>
                  </>
                )}

              </Box>
            </Box>
          )}

          {activePanel === 'properties' && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
            >
              <Box
                sx={{
                  px: 3,
                  pt: 3,
                  pb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
                  Properties
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  '&::-webkit-scrollbar': {
                    width: 8,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(17, 24, 39, 0.2)',
                    borderRadius: 8,
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'rgba(17, 24, 39, 0.35)',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <PropertiesPanel
                  selectedElement={selectedElement ?? null}
                  onClose={onClosePanel}
                  onSignRotate={onSignRotate}
                  onSignResize={onSignResize}
                  onSignMove={onSignMove}
                  onSignCalloutMove={onSignCalloutMove}
                  onSignDelete={onSignDelete}
                  onArrowDistanceChange={(id, dist) => onArrowDistanceChange?.(id, dist)}
                  onArrowLabelVisibilityChange={onArrowLabelVisibilityChange}
                  onArrowDelete={onArrowDelete}
                  onDelineatorUpdate={onDelineatorUpdate}
                  onDelineatorDelete={onDelineatorDelete}
                  onWorkzoneDelete={onWorkzoneDelete}
                  onRoadOverlaySettingsChange={onRoadOverlaySettingsChange}
                />
              </Box>
            </Box>
          )}

          {activePanel === 'chat' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <TrafficSpecialistChat
                open={true}
                onClose={onOpenTrafficSpecialist}
                signPlacement={signPlacement}
                workzones={workzones}
                variant="panel"
              />
            </Box>
          )}

          {activePanel === 'quota' && (
            <Box
              sx={{
                position: 'fixed',
                left: 100,
                bottom: 38,
                height: 'fit-content',
                width: '528px',
                display: 'flex',
              }}
            >
              <QuotaWarning
                open={true}
                onClose={() => onClosePanel()}
                quotaDetails={quotaDetails}
                actionType="tgs_generated"
                canProceed={true}
                onProceed={() => {}}
              />
            </Box>
          )}

          {activePanel === 'export' && (
            <ExportPlanPanel
              open={true}
              onClose={onClosePanel}
              onExport={(format) => {
                onClosePanel();
              }}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyUniqueIds = (svgElement: SVGSVGElement, prefix: string) => {
  const idMap = new Map<string, string>();
  const classMap = new Map<string, string>();

  // Make IDs unique
  svgElement.querySelectorAll('[id]').forEach(el => {
    const oldId = el.getAttribute('id');
    if (!oldId) return;
    const newId = `${prefix}-${oldId}`;
    if (oldId === newId) return;
    idMap.set(oldId, newId);
    el.setAttribute('id', newId);
  });

  // Make CSS class names unique (THIS IS THE KEY FIX FOR COLORS!)
  svgElement.querySelectorAll('[class]').forEach(el => {
    const classList = el.getAttribute('class');
    if (!classList) return;
    const classes = classList.split(/\s+/).filter(c => c);
    const newClasses = classes.map(oldClass => {
      if (!classMap.has(oldClass)) {
        const newClass = `${prefix}-${oldClass}`;
        classMap.set(oldClass, newClass);
      }
      return classMap.get(oldClass)!;
    });
    el.setAttribute('class', newClasses.join(' '));
  });

  const updateValue = (value: string | null) => {
    if (!value) return value;
    let updated = value;
    idMap.forEach((newId, oldId) => {
      const oldHash = `#${oldId}`;
      const newHash = `#${newId}`;
      const urlRegex = new RegExp(`url\\(${escapeRegExp(oldHash)}\\)`, 'g');
      updated = updated.replace(urlRegex, `url(${newHash})`);

      const hashRegex = new RegExp(escapeRegExp(oldHash), 'g');
      updated = updated.replace(hashRegex, newHash);
    });
    return updated;
  };

  const attributeNames = [
    'fill',
    'stroke',
    'filter',
    'clip-path',
    'mask',
    'marker-start',
    'marker-mid',
    'marker-end',
    'style',
    'xlink:href',
    'href',
  ];

  svgElement.querySelectorAll('*').forEach(el => {
    attributeNames.forEach(name => {
      if (!el.hasAttribute(name)) return;
      const value = el.getAttribute(name);
      const updated = updateValue(value);
      if (updated !== value && updated != null) {
        el.setAttribute(name, updated);
      }
    });
  });

  // Update CSS class selectors in <style> tags
  svgElement.querySelectorAll('style').forEach(styleEl => {
    const cssText = styleEl.textContent;
    if (!cssText) return;
    let updatedCss = cssText;
    
    // Update ID references
    idMap.forEach((newId, oldId) => {
      const oldHash = `#${oldId}`;
      const newHash = `#${newId}`;
      const regex = new RegExp(escapeRegExp(oldHash), 'g');
      updatedCss = updatedCss.replace(regex, newHash);
    });
    
    // Update class selectors (THIS FIXES THE COLOR ISSUE!)
    classMap.forEach((newClass, oldClass) => {
      // Match .className but not .classname123 (word boundary)
      const regex = new RegExp(`\\.${escapeRegExp(oldClass)}(?![a-zA-Z0-9_-])`, 'g');
      updatedCss = updatedCss.replace(regex, `.${newClass}`);
    });
    
    styleEl.textContent = updatedCss;
  });
};
